import { Injectable } from '@angular/core';

export interface RoleFitDataItem {
  criteria: string;
  criteria_content: string;
  score: number | null;
}

// Maps the fixed dimension keys returned by getChipForIdealCandidate to display labels.
// Shared between hire-candidate-card (per-card build) and hire-candidate-list
// (bulk backfill for candidates that haven't rendered a card yet), so both stay in sync.
export const IDEAL_CANDIDATE_CHIP_LABELS: Record<string, string> = {
  current_jobrole: 'Current Job Role',
  skills: 'Skills',
  past_experiences: 'Past Experience',
  location: 'Location',
  totaltenure: 'Total Tenure',
  education: 'Education',
  industry: 'Industry',
  stability: 'Stability'
};

@Injectable({
  providedIn: 'root'
})
export class RoleFitDataService {
  // Keyed by candidateid so different candidates/instances never collide.
  private roleFitDataMap = new Map<string, RoleFitDataItem[]>();
  private overallScoreMap = new Map<string, number | null>();

  setRoleFitData(candidateId: string, data: RoleFitDataItem[]): void {
    if (!candidateId) return;
    this.roleFitDataMap.set(candidateId, data ?? []);
  }

  getRoleFitData(candidateId: string): RoleFitDataItem[] {
    return this.roleFitDataMap.get(candidateId) ?? [];
  }

  setOverallScore(candidateId: string, score: number | null): void {
    if (!candidateId) return;
    this.overallScoreMap.set(candidateId, score ?? null);
  }

  getOverallScore(candidateId: string): number | null {
    return this.overallScoreMap.get(candidateId) ?? null;
  }

  hasRoleFitData(candidateId: string): boolean {
    return this.roleFitDataMap.has(candidateId);
  }

  clearRoleFitData(candidateId: string): void {
    this.roleFitDataMap.delete(candidateId);
    this.overallScoreMap.delete(candidateId);
  }

  /**
   * Builds RoleFitDataItem[] from a screeningCriteriaSummary map + a candidate's
   * personSearchScore.screening_criteria list. Mirrors hire-candidate-card's
   * buildCriteriaChips() mapping, so a candidate backfilled in bulk (never rendered
   * as a card) ends up with identical data to one built from the card itself.
   */
  buildFromScreeningSummary(
    summary: Record<string, string>,
    screeningCriteria: { criteria: string; score: number }[]
  ): RoleFitDataItem[] {
    const scoreByKey: Record<string, number> = (screeningCriteria ?? []).reduce((acc, item) => {
      if (item?.criteria) {
        acc[item.criteria.toLowerCase()] = item.score;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(summary ?? {})
      .filter(([, value]) => (value as string)?.trim())
      .map(([key, value]) => ({
        criteria: key,
        criteria_content: value,
        score: scoreByKey[key.toLowerCase()] ?? null,
      }));
  }

  /**
   * Builds RoleFitDataItem[] from an ideal-candidate match object, mirroring
   * hire-candidate-card's buildIdealCandidateChips().
   */
  buildFromIdealCandidateMatch(match: Record<string, any>): RoleFitDataItem[] {
    return Object.entries(IDEAL_CANDIDATE_CHIP_LABELS).map(([key, label]) => ({
      criteria: key,
      criteria_content: label,
      score: match?.[key] ?? null,
    }));
  }

private idealCandidateMatchMap = new Map<string, any>();

setIdealCandidateMatch(id: string, match: any): void {
  this.idealCandidateMatchMap.set(id, match);
}

getIdealCandidateMatch(id: string): any {
  return this.idealCandidateMatchMap.get(id) ?? null;
}
}
